import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Button,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Collapse
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useUser } from '../../../contexts/UserContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useNetwork } from '../../../utils/network';
import { useShowSnackbar } from '../../../utils/snackbar';
import { CARD_ART_BASE } from '../NFTCollection/components/CardInspectModal';
import { holdMessage } from '../../../utils/securityHold';
import { useWallet } from '@solana/wallet-adapter-react';
import { Transaction } from '@solana/web3.js';
import { io } from 'socket.io-client';
import { ActivityGrid } from './components/ActivityGrid';
import { MarketplaceNav } from './components/MarketplaceNav';
import { ArenaPager } from '../../../components/ArenaPager';
import { ListNftModal } from './components/ListNftModal';
import { MaintenanceMode } from './components/MaintenanceMode';
import { MyListingCard } from './components/MyListingCard';
import { NftCard } from './components/NftCard';
import { NftGroupCard } from './components/NftGroupCard';
import { PurchaseNftModal } from './components/PurchaseNftModal';
import { track, trackError } from '../../../utils/track';
import {
  MarketplaceActivity,
  MarketplaceListing,
  NftGroup,
  UserNft
} from './types';
import { Buffer } from 'buffer';

const MONO = 'ui-monospace, "SF Mono", Menlo, Consolas, monospace';

// One dark (arena) theme for the whole marketplace — every MUI control (search,
// filter selects, price inputs, pagination, the modals) renders dark/cyan
// instead of the app's light default.
const mktTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#38e8ff' },
    error: { main: '#ff6b6b' },
    background: { paper: '#0b1119', default: '#070b11' },
    text: { primary: '#e8f4fb', secondary: '#8ea3b3' }
  },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: 'rgba(7, 11, 17, 0.55)',
          '& fieldset': { borderColor: 'rgba(56, 232, 255, 0.28)' },
          '&:hover fieldset': { borderColor: 'rgba(56, 232, 255, 0.5)' },
          '&.Mui-focused fieldset': { borderColor: '#38e8ff' }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: '#8ea3b3', '&.Mui-focused': { color: '#38e8ff' } }
      }
    }
  }
});

// Arena panel chrome.
const panelSx = {
  borderRadius: '16px',
  border: '1px solid rgba(56, 232, 255, 0.22)',
  background:
    'linear-gradient(180deg, rgba(11, 17, 25, 0.74), rgba(7, 11, 17, 0.78))',
  boxShadow: '0 18px 40px -26px rgba(0, 0, 0, 0.9)'
} as const;

export const Marketplace = () => {
  const { user, setUser } = useUser();
  const { isLoggedIn } = useAuth();
  const { get, post } = useNetwork();
  const { showSnackbar } = useShowSnackbar();
  const { publicKey, signTransaction, connected } = useWallet();

  // State management - ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  const [activeTab, setActiveTab] = useState(0);
  const [listings, setListings] = useState<MarketplaceListing[]>([]);
  const [allListings, setAllListings] = useState<MarketplaceListing[]>([]); // Store all listings for filtering
  const [filteredListings, setFilteredListings] = useState<
    MarketplaceListing[]
  >([]);
  const [myListings, setMyListings] = useState<MarketplaceListing[]>([]);
  const [userNfts, setUserNfts] = useState<UserNft[]>([]);
  const [filteredUserNfts, setFilteredUserNfts] = useState<UserNft[]>([]);
  const [marketplaceActivity, setMarketplaceActivity] = useState<
    MarketplaceActivity[]
  >([]);
  const [filteredMarketplaceActivity, setFilteredMarketplaceActivity] =
    useState<MarketplaceActivity[]>([]);

  // Grouped NFT states
  const [nftGroups, setNftGroups] = useState<NftGroup[]>([]);
  const [viewMode, setViewMode] = useState<'grouped' | 'individual'>('grouped'); // New view mode state
  const location = useLocation();
  const [selectedNftGroup, setSelectedNftGroup] = useState<string | null>(null); // For detailed view

  // Maintenance mode state
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Search state for grouped view
  const [groupedSearchTerm, setGroupedSearchTerm] = useState('');
  const [marketplaceStats, setMarketplaceStats] = useState({
    totalListings: 0,
    activeListings: 0,
    soldListings: 0,
    cancelledListings: 0,
    totalVolume: '0.00000'
  });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasActiveFilters, setHasActiveFilters] = useState(false); // Track if filters are active

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  /* What the member typed drives the input; this drives the actual filtering. Re-filtering the
     whole marketplace on every keystroke is what made searching feel like it was constantly
     reloading — a settle of ~250ms means one pass per word rather than one per letter, and the
     box itself stays perfectly responsive because it still renders `searchTerm`. */
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 250);
    return () => clearTimeout(t);
  }, [searchTerm]);
  const [rarityFilter, setRarityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [factionFilter, setFactionFilter] = useState('');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [showFilters, setShowFilters] = useState(false);

  // Modal states
  const [isListingModalOpen, setIsListingModalOpen] = useState(false);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [selectedNft, setSelectedNft] = useState<UserNft | null>(null);
  const [selectedListing, setSelectedListing] =
    useState<MarketplaceListing | null>(null);
  const [listingPrice, setListingPrice] = useState('');
  const [listingQuantity, setListingQuantity] = useState(1);
  const [quantityInput, setQuantityInput] = useState('1'); // Separate state for input field
  const [isProcessing, setIsProcessing] = useState(false);
  const [cancellingListings, setCancellingListings] = useState<Set<number>>(
    new Set()
  );
  const [showPriceCalculator, setShowPriceCalculator] = useState(false);

  // Last sold price state
  const [lastSoldPrice, setLastSoldPrice] = useState<{
    hasLastSale: boolean;
    lastSoldPrice?: string;
    soldDate?: string;
    seller?: string;
    buyer?: string;
    message?: string;
  } | null>(null);
  const [loadingLastPrice, setLoadingLastPrice] = useState(false);

  useEffect(() => {
    const getUserBalances = async () => {
      try {
        const result = await get('/secure-tokens/balances', true);
        const balances = result?.balances;

        if (!balances) return;

        setUser({
          ...user,
          tokenAmount: balances.tokenAmount ?? user.tokenAmount,
          earnedTokens: balances.earnedTokens ?? user.earnedTokens
        });
      } catch (error: any) {
        console.log('Failed to fetch user balances:', error);
      }
    };

    getUserBalances();
  }, []);

  // Check if any filters are active
  useEffect(() => {
    const filtersActive = !!(
      searchTerm ||
      rarityFilter ||
      typeFilter ||
      factionFilter ||
      priceRange.min ||
      priceRange.max
    );
    setHasActiveFilters(filtersActive);

    // Fetch the full listing set the first time filters/search become active (lazy). The
    // initial screen shows only the latest page rather than pulling the whole marketplace up
    // front. fetchAllListings guards itself against overlapping and repeat calls, so typing
    // fast can no longer stack a fetch per keystroke.
    if (filtersActive && activeTab === 0) {
      fetchAllListings();
    }
  }, [
    searchTerm,
    rarityFilter,
    typeFilter,
    factionFilter,
    priceRange,
    activeTab
  ]);

  // Filter listings based on search and filters
  useEffect(() => {
    /* Search across everything once the full set has loaded; until then keep filtering the page
       we already have. Switching to `allListings` the instant a filter goes active meant that
       while the background fetch was still running the source array was empty, so the grid
       flashed "no results" on the first keystroke and then repopulated — read by members as the
       marketplace reloading under them. */
    const fullSetReady = allListings.length > 0;
    let sourceListings = hasActiveFilters && fullSetReady ? allListings : listings;
    let filtered = sourceListings;

    // Only apply filters if we're on the Browse Marketplace tab
    if (activeTab === 0) {
      // Search filter
      if (debouncedSearchTerm) {
        filtered = filtered.filter(
          (listing) =>
            listing.nft.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
            listing.nft.type.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
            listing.nft.faction.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
        );
      }

      // Rarity filter
      if (rarityFilter) {
        filtered = filtered.filter(
          (listing) => listing.nft.rarity === rarityFilter
        );
      }

      // Type filter
      if (typeFilter) {
        filtered = filtered.filter(
          (listing) => listing.nft.type === typeFilter
        );
      }

      // Faction filter
      if (factionFilter) {
        filtered = filtered.filter(
          (listing) => listing.nft.faction === factionFilter
        );
      }

      // Price range filter
      if (priceRange.min || priceRange.max) {
        filtered = filtered.filter((listing) => {
          const price = parseFloat(listing.priceInINC);
          const min = priceRange.min ? parseFloat(priceRange.min) : 0;
          const max = priceRange.max ? parseFloat(priceRange.max) : Infinity;
          return price >= min && price <= max;
        });
      }
    }

    // Sort by price (cheapest first) when filtering by rarity, type, or faction
    if (activeTab === 0 && (rarityFilter || typeFilter || factionFilter)) {
      filtered = filtered.sort((a, b) => {
        const priceA = parseFloat(a.priceInINC);
        const priceB = parseFloat(b.priceInINC);
        return priceA - priceB; // Sort ascending (cheapest first)
      });
    }

    setFilteredListings(filtered);

    // Calculate pagination for filtered results
    if (hasActiveFilters && activeTab === 0) {
      const itemsPerPage = 12;
      const totalFilteredPages = Math.ceil(filtered.length / itemsPerPage);
      setTotalPages(totalFilteredPages);

      // Reset to page 1 if current page exceeds total pages
      if (currentPage > totalFilteredPages && totalFilteredPages > 0) {
        setCurrentPage(1);
      }
    }
  }, [
    allListings,
    listings,
    debouncedSearchTerm,
    rarityFilter,
    typeFilter,
    factionFilter,
    priceRange,
    hasActiveFilters,
    activeTab,
    currentPage
  ]);

  // Filter user's NFTs based on search and filters
  useEffect(() => {
    let filtered = userNfts;

    // Filter out NFTs with 0 owned (sold NFTs)
    filtered = filtered.filter((userNft) => userNft.owned > 0);

    // Search filter
    if (debouncedSearchTerm) {
      filtered = filtered.filter(
        (userNft) =>
          (userNft.nft.name &&
            userNft.nft.name
              .toLowerCase()
              .includes(debouncedSearchTerm.toLowerCase())) ||
          (userNft.nft.type &&
            userNft.nft.type
              .toLowerCase()
              .includes(debouncedSearchTerm.toLowerCase())) ||
          (userNft.nft.faction &&
            userNft.nft.faction
              .toLowerCase()
              .includes(debouncedSearchTerm.toLowerCase()))
      );
    }

    // Rarity filter
    if (rarityFilter) {
      filtered = filtered.filter(
        (userNft) => userNft.nft.rarity === rarityFilter
      );
    }

    // Type filter
    if (typeFilter) {
      filtered = filtered.filter((userNft) => userNft.nft.type === typeFilter);
    }

    // Faction filter
    if (factionFilter) {
      filtered = filtered.filter(
        (userNft) => userNft.nft.faction === factionFilter
      );
    }

    setFilteredUserNfts(filtered);
  }, [userNfts, debouncedSearchTerm, rarityFilter, typeFilter, factionFilter]);

  // Filter marketplace activity based on search and filters
  useEffect(() => {
    let filtered = marketplaceActivity;

    // Search filter
    if (debouncedSearchTerm) {
      filtered = filtered.filter(
        (activity) =>
          activity.nft.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          activity.nft.type.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          activity.seller.username
            .toLowerCase()
            .includes(debouncedSearchTerm.toLowerCase()) ||
          (activity.buyer &&
            activity.buyer.username
              .toLowerCase()
              .includes(debouncedSearchTerm.toLowerCase()))
      );
    }

    // Rarity filter
    if (rarityFilter) {
      filtered = filtered.filter(
        (activity) => activity.nft.rarity === rarityFilter
      );
    }

    // Type filter
    if (typeFilter) {
      filtered = filtered.filter(
        (activity) => activity.nft.type === typeFilter
      );
    }

    // Status filter (using faction filter select for status)
    if (factionFilter) {
      filtered = filtered.filter(
        (activity) => activity.status === factionFilter
      );
    }

    // Price range filter
    if (priceRange.min || priceRange.max) {
      filtered = filtered.filter((activity) => {
        const price = parseFloat(activity.priceInINC);
        const min = priceRange.min ? parseFloat(priceRange.min) : 0;
        const max = priceRange.max ? parseFloat(priceRange.max) : Infinity;
        return price >= min && price <= max;
      });
    }

    setFilteredMarketplaceActivity(filtered);
  }, [
    marketplaceActivity,
    debouncedSearchTerm,
    rarityFilter,
    typeFilter,
    factionFilter,
    priceRange
  ]);

  // Fetch marketplace listings
  const fetchListings = async (page = 1) => {
    try {
      setLoading(true);
      const response = await get(
        `/marketplace/listings?page=${page}&limit=20`,
        true
      );
      setListings(response.listings);
      setCurrentPage(response.pagination.currentPage);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error('Error fetching listings:', error);
      showSnackbar('Failed to load marketplace listings', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all marketplace listings for filtering
  /* Loads the whole listing set once, so searching can filter across everything rather than
     just the current page.
     Two things went wrong here and both looked like "the marketplace keeps refreshing":
       1. Nothing guarded against overlapping calls. The caller's guard was
          `allListings.length === 0`, which stays true for as long as the first request is in
          flight — so every extra keystroke fired another full fetch of the entire marketplace.
       2. On failure it wiped the member's typed query and every filter. From the member's side
          the search box simply erased itself mid-word, which is exactly the reported symptom.
     Now: one request at a time, and a failure never touches what the member typed. */
  const allListingsLoadedRef = React.useRef(false);
  const allListingsInFlightRef = React.useRef(false);

  const fetchAllListings = async () => {
    if (allListingsInFlightRef.current || allListingsLoadedRef.current) return;
    allListingsInFlightRef.current = true;
    try {
      const response = await get(
        '/marketplace/all-listings-for-filtering',
        true
      ); // Fetch all listings without pagination

      setAllListings(response.listings || []);
      // a flag, not `length === 0`: a genuinely empty marketplace would otherwise
      // re-trigger this fetch on every subsequent keystroke, forever
      allListingsLoadedRef.current = true;
    } catch (error) {
      console.error('Error fetching all listings for filtering:', error);
      showSnackbar(
        'Could not load the full marketplace to search across — showing the current page only. Try again shortly.',
        'warning'
      );
      // deliberately does NOT clear searchTerm/filters: destroying what someone just typed
      // because a background request failed is worse than the degraded results
    } finally {
      allListingsInFlightRef.current = false;
    }
  };

  // Fetch user's listings
  const fetchMyListings = async () => {
    try {
      const response = await get('/marketplace/my-listings', true);
      setMyListings(response.listings);
    } catch (error) {
      console.error('Error fetching my listings:', error);
      showSnackbar('Failed to load your listings', 'error');
    }
  };

  // Fetch grouped NFT listings
  /* THE FOCUS BUG. `loading` gates the whole content subtree — spinner on one branch, the
     entire page (search box included) on the other. Every debounced keystroke called
     setLoading(true), so React unmounted the input the member was typing into and mounted a
     fresh one when the results came back. The caret went with it: type, pause, and you are
     silently thrown out of the box mid-word.
     So a refetch only blanks the page when there is nothing on screen yet. Once results have
     been shown once, searching refreshes them in place and the input keeps both its focus and
     its caret position. */
  const groupsLoadedRef = React.useRef(false);
  const [groupsRefreshing, setGroupsRefreshing] = useState(false);

  const fetchGroupedListings = async (searchTerm?: string) => {
    const inPlace = groupsLoadedRef.current;
    try {
      if (inPlace) setGroupsRefreshing(true);
      else setLoading(true);
      const searchParam = searchTerm || groupedSearchTerm;
      const url = searchParam
        ? `/marketplace/grouped-listings?search=${encodeURIComponent(searchParam)}`
        : '/marketplace/grouped-listings';

      const response = await get(url, true);
      setNftGroups(response.nftGroups);
      setMaintenanceMode(false);
    } catch (error: any) {
      console.error('Error fetching grouped listings:', error);
      if (
        error.response?.status === 503 &&
        error.response?.data?.maintenanceMode
      ) {
        setMaintenanceMode(true);
      } else {
        showSnackbar('Failed to load grouped Digital Collectibles listings', 'error');
      }
    } finally {
      // marked loaded even on failure: a failed search must not send the NEXT one back to
      // blanking the page, which is how the focus loss would quietly return
      groupsLoadedRef.current = true;
      setGroupsRefreshing(false);
      setLoading(false);
    }
  };

  // Fetch listings for a specific NFT type
  const fetchNftTypeListings = async (nftName: string) => {
    try {
      setLoading(true);
      const response = await get(
        `/marketplace/nft/${encodeURIComponent(nftName)}/listings`,
        true
      );
      setFilteredListings(response.listings);
      setListings(response.listings);
    } catch (error) {
      console.error(`Error fetching listings for NFT ${nftName}:`, error);
      showSnackbar(`Failed to load ${nftName} listings`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch user's NFTs
  const fetchUserNfts = async () => {
    try {
      const response = await get('/nft/user', true);

      // Transform the response to match the expected format
      const transformedNfts = response
        .map((userNft: any) => {
          return {
            nft: userNft.nft || {},
            owned: userNft.amount || 0,
            locked: userNft.locked || 0
          };
        })
        .filter((nft: any) => nft.nft && nft.nft.id); // Filter out NFTs without valid data

      setUserNfts(transformedNfts);
    } catch (error) {
      console.error('Error fetching user NFTs:', error);
      showSnackbar('Failed to load your Digital Collectibles', 'error');
    }
  };

  // Fetch marketplace activity
  const fetchMarketplaceActivity = async () => {
    try {
      const response = await get('/marketplace/activity', true);
      setMarketplaceActivity(response.marketplaceActivity || []);
      setMarketplaceStats(
        response.stats || {
          totalListings: 0,
          activeListings: 0,
          soldListings: 0,
          cancelledListings: 0,
          totalVolume: '0.00000'
        }
      );
    } catch (error) {
      console.error('Error fetching marketplace activity:', error);
      showSnackbar('Failed to load marketplace activity', 'error');
    }
  };

  // Check if user is admin
  useEffect(() => {
    if (user) {
      setIsAdmin(user.role === 'admin' || user.role === 'super_admin');
    }
  }, [user]);

  // Debounced search for grouped listings
  useEffect(() => {
    if (viewMode === 'grouped' && user) {
      const timeoutId = setTimeout(() => {
        fetchGroupedListings(groupedSearchTerm);
      }, 500); // 500ms debounce

      return () => clearTimeout(timeoutId);
    }
  }, [groupedSearchTerm, viewMode, user]);

  // Deep-link from the arena's "Buy on Marketplace": pre-fill the grouped search
  // with the inspected card so we open straight to its listings. The debounced
  // effect above then fetches. Runs once on mount.
  useEffect(() => {
    // router state from the same tab, or ?card= from the arena's new tab (2026-11-04)
    const card =
      (location.state as { card?: string } | null)?.card ||
      new URLSearchParams(location.search).get('card') ||
      '';
    if (card) {
      setViewMode('grouped');
      setGroupedSearchTerm(card);
      window.history.replaceState({}, '', location.pathname); // don't re-apply on refresh/back
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Fetch data for all authenticated users
    if (user) {
      if (viewMode === 'grouped') {
        fetchGroupedListings();
      } else if (selectedNftGroup) {
        fetchNftTypeListings(selectedNftGroup);
      } else {
        fetchListings();
      }
      fetchMyListings();
      fetchUserNfts();
      fetchMarketplaceActivity();
    }
  }, [user, viewMode, selectedNftGroup]);

  // Set up socket listeners for real-time marketplace updates
  useEffect(() => {
    // Connect to the main server socket for marketplace notifications
    const socket = io(
      process.env.REACT_APP_API_URL || 'http://localhost:4242',
      {
        withCredentials: true,
        transports: ['websocket', 'polling']
      }
    );

    // Listen for new listings
    socket.on('marketplace-listing-created', (data: any) => {
      // Refresh listings to show new listing - this is less disruptive for new listings
      if (hasActiveFilters) {
        fetchAllListings();
      } else {
        fetchListings(currentPage);
      }
    });

    // Listen for listing removals (sold/cancelled)
    socket.on('marketplace-listing-removed', (data: any) => {
      // Instead of full refresh, selectively remove the listing from local state
      // This prevents interrupting users browsing the marketplace
      const removedListingId = data.listingId;
      const removedNftId = data.nftId;

      // Remove from regular listings (individual view)
      setListings((prevListings) =>
        prevListings.filter((listing) => listing.id !== removedListingId)
      );

      // Remove from filtered listings if they exist
      setAllListings((prevAllListings) =>
        prevAllListings.filter((listing) => listing.id !== removedListingId)
      );

      // Remove from My Listings if it's there
      setMyListings((prevMyListings) =>
        prevMyListings.filter((listing) => listing.id !== removedListingId)
      );

      // Update grouped listings without full refresh - decrement counts and remove if needed
      setNftGroups((prevGroups) => {
        return prevGroups
          .map((group) => {
            if (group.nft.id === removedNftId) {
              const newTotalListings = group.totalListings - 1;

              // If this was the last listing for this NFT, remove the group entirely
              if (newTotalListings <= 0) {
                return null; // Will be filtered out below
              }

              // Otherwise, just decrement the count
              // Note: We keep the pricing info as-is since we'd need to fetch all remaining
              // listings to recalculate accurate price ranges. The counts are most important
              // for user experience, and prices will be accurate when they click to view details.
              return {
                ...group,
                totalListings: newTotalListings
              };
            }
            return group;
          })
          .filter((group): group is NftGroup => group !== null); // Remove null entries (groups with 0 listings)
      });

      // Update marketplace activity to reflect the change
      fetchMarketplaceActivity();
    });

    // Cleanup on unmount
    return () => {
      socket.disconnect();
    };
  }, [hasActiveFilters, currentPage]);

  // Sync quantity input when selectedNft changes
  useEffect(() => {
    if (selectedNft) {
      // Reset to 1 when opening modal for a new NFT
      setListingQuantity(1);
      setQuantityInput('1');
    }
  }, [selectedNft]);

  // Check if user is authenticated
  if (!user || !isLoggedIn) {
    return (
      <Box
        sx={{
          p: 3,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Authentication Required
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please log in to access the marketplace.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Get unique values for filters - handles different data structures
  const getUniqueValues = (
    source: any[],
    key: string,
    tabIndex: number = activeTab
  ) => {
    let values: (string | number)[] = [];

    if (tabIndex === 0) {
      // Browse Marketplace - MarketplaceListing[]
      // Always prioritize allListings for complete filter options
      const dataSource = allListings.length > 0 ? allListings : source;
      values = dataSource.flatMap((item) => (item.nft ? [item.nft[key]] : []));
    } else if (tabIndex === 2) {
      // List NFT - UserNft[]
      values = source.flatMap((item) => (item.nft ? [item.nft[key]] : []));
    } else if (tabIndex === 3) {
      // Activity Tracking - MarketplaceActivity[]
      if (key === 'faction') {
        // For activity tab, use faction filter for status
        values = source.flatMap((item) => [item.status]);
      } else {
        values = source.flatMap((item) => (item.nft ? [item.nft[key]] : []));
      }
    }

    const uniqueValues: (string | number)[] = [];
    values.forEach((value) => {
      if (value && !uniqueValues.includes(value)) {
        uniqueValues.push(value);
      }
    });

    // Sort rarities in a logical order for better UX
    if (key === 'rarity') {
      const rarityOrder = ['common', 'rare', 'epic', 'legendary', 'relic'];
      uniqueValues.sort((a, b) => {
        const aIndex = rarityOrder.indexOf(a.toString().toLowerCase());
        const bIndex = rarityOrder.indexOf(b.toString().toLowerCase());
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
    }

    return uniqueValues;
  };

  const handleTabChange = (newValue: number) => {
    setActiveTab(newValue);
    track('marketplace.tab', { tab: newValue });

    // Clear filters when switching tabs to avoid confusion
    setSearchTerm('');
    setRarityFilter('');
    setTypeFilter('');
    setFactionFilter('');
    setPriceRange({ min: '', max: '' });

    if (newValue === 0) {
      setCurrentPage(1);
      fetchListings(1);
      // Ensure we have all listings for complete filter options
      if (allListings.length === 0) {
        fetchAllListings();
      }
    } else if (newValue === 1) {
      fetchMyListings();
    } else if (newValue === 2) {
      fetchUserNfts();
    } else if (newValue === 3) {
      fetchMarketplaceActivity();
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
    fetchListings(page);
  };

  const handleFilteredPageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
    // No need to fetch new data since we already have all filtered results
  };

  const clearFilters = () => {
    setSearchTerm('');
    setRarityFilter('');
    setTypeFilter('');
    setFactionFilter('');
    setPriceRange({ min: '', max: '' });

    // Reset to page 1 and fetch paginated results when clearing filters
    if (activeTab === 0) {
      setCurrentPage(1);
      fetchListings(1);
    }
  };

  // Fetch last sold price for an NFT
  const fetchLastSoldPrice = async (nftId: number) => {
    setLoadingLastPrice(true);
    try {
      const response = await get(`/marketplace/last-sold-price/${nftId}`, true);
      setLastSoldPrice(response);
    } catch (error) {
      console.error('Error fetching last sold price:', error);
      setLastSoldPrice({
        hasLastSale: false,
        message: 'Could not fetch price history'
      });
    } finally {
      setLoadingLastPrice(false);
    }
  };

  // Handle cancelling a listing
  const handleCancelListing = async (listingId: number) => {
    // Prevent spam clicking - check if this listing is already being cancelled
    if (cancellingListings.has(listingId)) {
      return;
    }

    try {
      // Add this listing to the cancelling set
      setCancellingListings((prev) => new Set(prev).add(listingId));

      const response = await post(
        '/marketplace/cancel-listing',
        { listingId },
        true
      );
      showSnackbar(response.message, 'success');
      fetchMyListings();
      fetchUserNfts(); // Refresh user NFTs to show the returned NFT
      // Note: fetchMarketplaceActivity() and grouped listings will be updated by socket listener
      // Note: Marketplace listings will be updated by socket events in real-time
    } catch (error: any) {
      console.error('Error cancelling listing:', error);
      showSnackbar(
        error?.response?.data?.message || 'Failed to cancel listing',
        'error'
      );
    } finally {
      // Remove this listing from the cancelling set
      setCancellingListings((prev) => {
        const newSet = new Set(prev);
        newSet.delete(listingId);
        return newSet;
      });
    }
  };

  const getRarityColor = (rarity: string | undefined | null) => {
    if (!rarity) return '#6b7280';

    switch (rarity.toLowerCase()) {
      case 'common':
        return '#6b7280'; // Gray
      case 'rare':
        return '#d946ef'; // Purple-pinkish
      case 'epic':
        return '#22c55e'; // Green
      case 'legendary':
        return '#ef4444'; // Red
      case 'relic':
        return '#eab308'; // Gold
      default:
        return '#6b7280';
    }
  };

  const formatRarity = (rarity: string | undefined | null) => {
    if (!rarity) return 'Unknown';
    return rarity.charAt(0).toUpperCase() + rarity.slice(1).toLowerCase();
  };

  const renderSearchAndFilters = (
    options: { showPriceFilter?: boolean } = {}
  ) => {
    const { showPriceFilter = true } = options;
    let dataSource: any[] = [];
    let factionLabel = 'Faction';

    // Set data source based on active tab
    if (activeTab === 0) {
      // Always use allListings for filter options to show all available values, not just current page
      // If allListings is empty, fetch it and use listings as temporary fallback
      dataSource = allListings.length > 0 ? allListings : listings;

      // If allListings is empty but we have listings, trigger fetching all listings
      if (allListings.length === 0 && listings.length > 0) {
        fetchAllListings();
      }
    } else if (activeTab === 2) {
      dataSource = userNfts;
    } else if (activeTab === 3) {
      dataSource = marketplaceActivity;
      factionLabel = 'Status'; // Change label for activity tab
    }

    return (
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              placeholder="Search Digital Collectibles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Button
              variant="outlined"
              onClick={() => setShowFilters(!showFilters)}
              startIcon={<FilterIcon />}
              sx={{
                borderColor: '#38e8ff',
                color: '#38e8ff',
                '&:hover': {
                  borderColor: '#22d3ee',
                  backgroundColor: 'rgba(56, 232, 255, 0.1)'
                }
              }}
            >
              Filters
            </Button>
            {(searchTerm ||
              rarityFilter ||
              typeFilter ||
              factionFilter ||
              priceRange.min ||
              priceRange.max) && (
              <Button
                variant="text"
                onClick={clearFilters}
                sx={{ ml: 1, color: '#38e8ff' }}
              >
                Clear All
              </Button>
            )}
          </Grid>
        </Grid>

        <Collapse in={showFilters}>
          <Paper
            sx={{
              p: 2,
              mt: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: '#38e8ff' }}>
              Filter Options
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Rarity</InputLabel>
                  <Select
                    value={rarityFilter}
                    onChange={(e) => setRarityFilter(e.target.value)}
                    label="Rarity"
                  >
                    <MenuItem value="">All Rarities</MenuItem>
                    {getUniqueValues(dataSource, 'rarity', activeTab).map(
                      (rarity) => (
                        <MenuItem key={rarity} value={rarity}>
                          {rarity}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    label="Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {getUniqueValues(dataSource, 'type', activeTab).map(
                      (type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>{factionLabel}</InputLabel>
                  <Select
                    value={factionFilter}
                    onChange={(e) => setFactionFilter(e.target.value)}
                    label={factionLabel}
                  >
                    <MenuItem value="">
                      {activeTab === 3 ? 'All Statuses' : 'All Factions'}
                    </MenuItem>
                    {getUniqueValues(dataSource, 'faction', activeTab).map(
                      (item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>

              {showPriceFilter && (
                <Grid item xs={12} sm={6} md={3}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <TextField
                        label="Min Price (INC)"
                        type="number"
                        value={priceRange.min}
                        onChange={(e) =>
                          setPriceRange({ ...priceRange, min: e.target.value })
                        }
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Max Price (INC)"
                        type="number"
                        value={priceRange.max}
                        onChange={(e) =>
                          setPriceRange({ ...priceRange, max: e.target.value })
                        }
                        fullWidth
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Collapse>
      </Box>
    );
  };

  // Create DataGrid rows for activity tracking
  const marketplaceRows = filteredMarketplaceActivity.map(
    (activity, index) => ({
      id: activity.id || index,
      nftName: activity.nft.name,
      nftType: activity.nft.type,
      nftRarity: activity.nft.rarity,
      seller: activity.seller.username,
      buyer: activity.buyer?.username || 'N/A',
      priceInINC: activity.priceInINC,
      status: activity.status,
      date:
        activity.status === 'sold' ? activity.purchasedAt : activity.createdAt
    })
  );

  const applyBalancesFromConfirm = (confirmRes: any) => {
    const b = confirmRes?.balances;
    if (!b || !user) return;

    setUser({
      ...user,
      earnedTokens: b.earnedTokens ?? user.earnedTokens,
      tokenAmount: b.tokenAmount ?? user.tokenAmount
    });
  };

  // small helper: keep your validations consistent
  const getFinalListingQuantity = () => {
    if (!selectedNft) return 0;
    const parsed = parseInt(quantityInput);
    const qty =
      Number.isFinite(parsed) && parsed > 0 ? parsed : listingQuantity;
    return Math.max(1, Math.min(qty, Math.min(selectedNft.owned, 10)));
  };

  const validateListingBasics = () => {
    if (!selectedNft) {
      showSnackbar('Please select a Digital Collectible to list', 'error');
      return null;
    }

    if (!listingPrice) {
      showSnackbar('Please enter a valid price', 'error');
      return null;
    }

    const priceNum = parseFloat(listingPrice);
    if (!Number.isFinite(priceNum) || priceNum <= 0) {
      showSnackbar('Please enter a valid price', 'error');
      return null;
    }

    if (priceNum < 10) {
      showSnackbar('Minimum listing price is 10 INC tokens', 'error');
      return null;
    }

    const finalQty = getFinalListingQuantity();
    if (finalQty < 1 || finalQty > selectedNft.owned) {
      showSnackbar(
        `Invalid quantity. You can list between 1 and ${selectedNft.owned} Digital Collectibles`,
        'error'
      );
      return null;
    }
    if (finalQty > 10) {
      showSnackbar('Maximum bulk listing quantity is 10 at once', 'error');
      return null;
    }

    return { priceNum, finalQty };
  };

  const closeListingModalAndReset = () => {
    setIsListingModalOpen(false);
    setListingPrice('');
    setListingQuantity(1);
    setQuantityInput('1');
    setSelectedNft(null);
    setShowPriceCalculator(false);
    setLastSoldPrice(null);
  };

  const refreshAfterListing = async () => {
    // keep your existing refresh behavior
    fetchMyListings();
    fetchUserNfts();
    fetchMarketplaceActivity();
    // marketplace browse lists update via sockets; but this is fine
  };

  const getErrorMessage = (e: any) => {
    return (
      e?.response?.data?.message ||
      e?.response?.data?.error ||
      e?.message ||
      'Failed to purchase Digital Collectible'
    );
  };

  /**
   * 1) Pay with Earned Tokens (earnedTokens) - server flow
   *    This should call your new endpoint that supports listingFeeMode=earnedTokens
   */
  const handleListUsingEarnedTokens = async () => {
    const basics = validateListingBasics();
    if (!basics) return;

    const { finalQty } = basics;
    setIsProcessing(true);

    try {
      const createRes = await post(
        '/marketplace-listings/create-intent',
        {
          nftId: selectedNft!.nft.id,
          priceInINC: listingPrice,
          quantity: finalQty,
          mode: 'earnedTokens'
        },
        true
      );

      // ⚠️ THIS IS REQUIRED
      const confirmRes = await post(
        '/marketplace-listings/confirm',
        finalQty > 1
          ? {
              masterListingId: createRes.masterListingId,
              listingIds: createRes.listingIds
            }
          : {
              listingId: createRes.masterListingId
            },
        true
      );

      applyBalancesFromConfirm(confirmRes);

      showSnackbar(confirmRes.message, 'success');
      // CRM: a marketplace listing was created (supply signal). See utils/track.ts.
      track('marketplace.list');
      await refreshAfterListing();
      closeListingModalAndReset();
    } catch (e: any) {
      trackError('marketplace.tx', e);
      // the server's own words — a 48-hour hold after a 2FA reset says exactly that
      showSnackbar(holdMessage(e) ?? e?.data?.message ?? e?.response?.data?.message ?? 'Failed to list', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * 2) Pay with Internal INC Wallet (tokenAmount mirror) - server flow
   *    You said you want this disabled unless wallet connected (UX decision),
   *    but the actual listing is server-side.
   */
  const handleListUsingInternalWallet = async () => {
    const basics = validateListingBasics();
    if (!basics) return;

    if (!connected || !publicKey || !signTransaction) {
      showSnackbar('Please connect your wallet first', 'error');
      return;
    }

    const { finalQty } = basics;
    setIsProcessing(true);

    try {
      const createRes = await post(
        '/marketplace-listings/create-intent',
        {
          nftId: selectedNft!.nft.id,
          priceInINC: listingPrice,
          quantity: finalQty,
          mode: 'custody_inc',
          connectedWallet: publicKey.toBase58()
        },
        true
      );

      // ✅ decode base64 -> bytes safely
      const txBytes = Buffer.from(createRes.transactionBase64, 'base64');
      const tx = Transaction.from(txBytes);

      const signed = await signTransaction(tx);

      // ✅ encode bytes -> base64 safely
      const signedTxBytes = signed.serialize({
        requireAllSignatures: false,
        verifySignatures: false
      });
      const signedTransactionBase64 =
        Buffer.from(signedTxBytes).toString('base64');

      const confirmRes = await post(
        '/marketplace-listings/confirm',
        finalQty > 1
          ? {
              masterListingId: createRes.masterListingId,
              listingIds: createRes.listingIds,
              signedTransactionBase64: signedTransactionBase64,
              connectedWallet: publicKey.toBase58()
            }
          : {
              listingId: createRes.masterListingId,
              signedTransactionBase64: signedTransactionBase64,
              connectedWallet: publicKey.toBase58()
            },
        true
      );

      applyBalancesFromConfirm(confirmRes);

      showSnackbar(confirmRes.message, 'success');
      // CRM: a marketplace listing was created (supply signal). See utils/track.ts.
      track('marketplace.list');
      await refreshAfterListing();
      closeListingModalAndReset();
    } catch (e: any) {
      trackError('marketplace.tx', e);
      // the server's own words — a 48-hour hold after a 2FA reset says exactly that
      showSnackbar(holdMessage(e) ?? e?.data?.message ?? e?.response?.data?.message ?? 'Failed to list', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * 3) Pay with Connected Wallet (external) - tx create -> sign -> confirm
   */
  const handleListUsingConnectedWallet = async () => {
    const basics = validateListingBasics();
    if (!basics) return;

    if (!connected || !publicKey || !signTransaction) {
      showSnackbar('Please connect your wallet first', 'error');
      return;
    }

    const { finalQty } = basics;
    setIsProcessing(true);

    try {
      const createRes = await post(
        '/marketplace-listings/create-intent',
        {
          nftId: selectedNft!.nft.id,
          priceInINC: listingPrice,
          quantity: finalQty,
          mode: 'external_inc',
          connectedWallet: publicKey.toBase58()
        },
        true
      );

      const tx = Transaction.from(
        Uint8Array.from(atob(createRes.transactionBase64), (c) =>
          c.charCodeAt(0)
        )
      );

      const signed = await signTransaction(tx);

      const signedTxBase64 = btoa(
        String.fromCharCode(
          ...signed.serialize({
            requireAllSignatures: false,
            verifySignatures: false
          })
        )
      );

      const confirmRes = await post(
        '/marketplace-listings/confirm',
        finalQty > 1
          ? {
              masterListingId: createRes.masterListingId,
              listingIds: createRes.listingIds,
              signedTransactionBase64: signedTxBase64,
              connectedWallet: publicKey.toBase58()
            }
          : {
              listingId: createRes.masterListingId,
              signedTransactionBase64: signedTxBase64,
              connectedWallet: publicKey.toBase58()
            },
        true
      );

      showSnackbar(confirmRes.message, 'success');
      // CRM: a marketplace listing was created (supply signal). See utils/track.ts.
      track('marketplace.list');
      await refreshAfterListing();
      closeListingModalAndReset();
    } catch (e: any) {
      trackError('marketplace.tx', e);
      // the server's own words — a 48-hour hold after a 2FA reset says exactly that
      showSnackbar(holdMessage(e) ?? e?.data?.message ?? e?.response?.data?.message ?? 'Failed to list', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePurchaseUsingEarnedTokens = async () => {
    if (!selectedListing) return;

    setIsProcessing(true);
    try {
      const createRes = await post(
        '/marketplace-listings/purchase-intent',
        { listingId: selectedListing.id, mode: 'earnedTokens' },
        true
      );

      const confirmRes = await post(
        '/marketplace-listings/purchase-confirm',
        { listingId: createRes.listingId }, // ✅ FIX
        true
      );

      applyBalancesFromConfirm(confirmRes);
      showSnackbar(confirmRes.message, 'success');
      // CRM: a marketplace purchase confirmed (revenue signal). See utils/track.ts.
      track('marketplace.purchase', { listingId: selectedListing?.id });

      fetchUserNfts();
      fetchMarketplaceActivity();
      fetchListings();

      setIsPurchaseModalOpen(false);
      setSelectedListing(null);
    } catch (e: any) {
      trackError('marketplace.tx', e);
      const msg = getErrorMessage(e);

      showSnackbar(msg, 'error');

      // If listing is gone, hard refresh marketplace state
      if (msg.toLowerCase().includes('no longer available')) {
        setIsPurchaseModalOpen(false);
        setSelectedListing(null);
        window.location.reload(); // explained below
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePurchaseUsingInternalWallet = async () => {
    if (!selectedListing) return;

    if (!connected || !publicKey || !signTransaction) {
      showSnackbar('Please connect your wallet first', 'error');
      return;
    }

    setIsProcessing(true);
    try {
      const createRes = await post(
        '/marketplace-listings/purchase-intent',
        {
          listingId: selectedListing.id,
          mode: 'custody_inc',
          connectedWallet: publicKey.toBase58()
        },
        true
      );

      const tx = Transaction.from(
        Uint8Array.from(atob(createRes.transactionBase64), (c) =>
          c.charCodeAt(0)
        )
      );

      const signed = await signTransaction(tx);

      const signedTxBase64 = btoa(
        String.fromCharCode(
          ...signed.serialize({
            requireAllSignatures: false,
            verifySignatures: false
          })
        )
      );

      const confirmRes = await post(
        '/marketplace-listings/purchase-confirm',
        {
          listingId: createRes.listingId, // ✅ FIX
          signedTransactionBase64: signedTxBase64,
          connectedWallet: publicKey.toBase58()
        },
        true
      );

      applyBalancesFromConfirm(confirmRes);
      showSnackbar(confirmRes.message, 'success');
      // CRM: a marketplace purchase confirmed (revenue signal). See utils/track.ts.
      track('marketplace.purchase', { listingId: selectedListing?.id });

      fetchUserNfts();
      fetchMarketplaceActivity();
      fetchListings();

      setIsPurchaseModalOpen(false);
      setSelectedListing(null);
    } catch (e: any) {
      trackError('marketplace.tx', e);
      const msg = getErrorMessage(e);

      showSnackbar(msg, 'error');

      // If listing is gone, hard refresh marketplace state
      if (msg.toLowerCase().includes('no longer available')) {
        setIsPurchaseModalOpen(false);
        setSelectedListing(null);
        window.location.reload(); // explained below
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePurchaseUsingConnectedWallet = async () => {
    if (!selectedListing) return;

    if (!connected || !publicKey || !signTransaction) {
      showSnackbar('Please connect your wallet first', 'error');
      return;
    }

    setIsProcessing(true);
    try {
      const createRes = await post(
        '/marketplace-listings/purchase-intent',
        {
          listingId: selectedListing.id,
          mode: 'external_inc',
          connectedWallet: publicKey.toBase58()
        },
        true
      );

      const tx = Transaction.from(
        Uint8Array.from(atob(createRes.transactionBase64), (c) =>
          c.charCodeAt(0)
        )
      );

      const signed = await signTransaction(tx);

      const signedTxBase64 = btoa(
        String.fromCharCode(
          ...signed.serialize({
            requireAllSignatures: false,
            verifySignatures: false
          })
        )
      );

      const confirmRes = await post(
        '/marketplace-listings/purchase-confirm',
        {
          listingId: createRes.listingId, // ✅ FIX
          signedTransactionBase64: signedTxBase64,
          connectedWallet: publicKey.toBase58()
        },
        true
      );

      applyBalancesFromConfirm(confirmRes);
      showSnackbar(confirmRes.message, 'success');
      // CRM: a marketplace purchase confirmed (revenue signal). See utils/track.ts.
      track('marketplace.purchase', { listingId: selectedListing?.id });

      fetchUserNfts();
      fetchMarketplaceActivity();
      fetchListings();

      setIsPurchaseModalOpen(false);
      setSelectedListing(null);
    } catch (e: any) {
      trackError('marketplace.tx', e);
      const msg = getErrorMessage(e);

      showSnackbar(msg, 'error');

      // If listing is gone, hard refresh marketplace state
      if (msg.toLowerCase().includes('no longer available')) {
        setIsPurchaseModalOpen(false);
        setSelectedListing(null);
        window.location.reload(); // explained below
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <ThemeProvider theme={mktTheme}>
    <Box sx={{ p: 3, minHeight: '100vh' }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            font: '800 10px ui-monospace, Menlo, Consolas, monospace',
            letterSpacing: '0.4em',
            textTransform: 'uppercase',
            color: '#ffcf40'
          }}
        >
          Marketplace
        </Typography>
        <Typography
          sx={{
            mt: 1.25,
            fontWeight: 900,
            fontSize: 'clamp(24px, 3.4vw, 36px)',
            lineHeight: 1,
            letterSpacing: '-0.02em',
            color: '#e8f4fb'
          }}
        >
          Trade your{' '}
          <Box component="span" sx={{ color: '#38e8ff' }}>
            cards
          </Box>
        </Typography>
        <Typography
          sx={{
            mt: 1.25,
            font: '600 13px/1.55 ui-monospace, Menlo, Consolas, monospace',
            color: '#8ea3b3',
            maxWidth: 640
          }}
        >
          Buy and sell Digital Collectibles with other players using INC tokens.
        </Typography>
      </Box>

      <Alert
        severity="info"
        variant="outlined"
        sx={{
          mb: 3,
          borderRadius: '12px',
          border: '1px solid rgba(56, 232, 255, 0.25)',
          background: 'rgba(56, 232, 255, 0.06)',
          '& .MuiAlert-icon': { color: '#38e8ff' }
        }}
      >
        <Typography sx={{ font: `600 13px ${MONO}`, color: '#c9dbe6' }}>
          <Box component="span" sx={{ color: '#38e8ff', fontWeight: 800 }}>
            Listing fee:
          </Box>{' '}
          a 5% INC fee is charged for listing a Digital Collectible.
        </Typography>
      </Alert>

      <MarketplaceNav
        activeTab={activeTab}
        onSelect={handleTabChange}
        activeListings={marketplaceStats.activeListings}
        myListingsCount={
          myListings.filter((l) => l.status !== 'fee_pending').length
        }
        /* Every copy, not every kind: a member with 883 different cards and 8,348
           copies read "883 · you own" as 883 cards they could list. The tiles
           below are one per kind, each saying how many of it they hold. */
        ownedCount={userNfts.reduce((total, n) => total + n.owned, 0)}
        salesCount={marketplaceStats.soldListings}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress sx={{ color: '#38e8ff' }} />
        </Box>
      ) : maintenanceMode && !isAdmin ? (
        <MaintenanceMode isAdmin={isAdmin} />
      ) : (
        <Box>
          {/* Admin maintenance notice */}
          {maintenanceMode && isAdmin && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                backgroundColor: 'rgba(255, 193, 7, 0.1)',
                borderRadius: 1,
                border: '1px solid rgba(255, 193, 7, 0.5)',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              <AdminIcon sx={{ color: '#ffc107' }} />
              <Typography
                variant="body1"
                sx={{ color: '#ffc107', fontWeight: 'medium' }}
              >
                🔧 Marketplace is in maintenance mode - Admin access enabled
              </Typography>
            </Box>
          )}

          {/* Browse Marketplace Tab */}
          {activeTab === 0 && (
            <Box>
              {/* Search for Grouped View */}
              {viewMode === 'grouped' && (
                <Box sx={{ mb: 3 }}>
                  <TextField
                    placeholder="Search Digital Collectibles..."
                    value={groupedSearchTerm}
                    onChange={(e) => setGroupedSearchTerm(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        fetchGroupedListings(groupedSearchTerm);
                      }
                    }}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (groupsRefreshing || groupedSearchTerm) && (
                        <InputAdornment position="end">
                          {/* Searching now shows here instead of replacing the page with a
                              spinner — the member can see it working without losing their
                              place in the box they are typing in. */}
                          {groupsRefreshing && (
                            <CircularProgress
                              size={16}
                              sx={{ color: '#38e8ff', mr: groupedSearchTerm ? 1 : 0 }}
                            />
                          )}
                          {groupedSearchTerm && (
                          <IconButton
                            onClick={() => {
                              setGroupedSearchTerm('');
                              fetchGroupedListings('');
                            }}
                            edge="end"
                          >
                            <CloseIcon />
                          </IconButton>
                          )}
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#38e8ff'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#38e8ff'
                        }
                      }
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 2
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Search by Digital Collectible name, type, faction, or rarity
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => fetchGroupedListings(groupedSearchTerm)}
                      sx={{
                        backgroundColor: '#38e8ff',
                        '&:hover': { backgroundColor: '#22d3ee' }
                      }}
                      startIcon={<SearchIcon />}
                    >
                      Search
                    </Button>
                  </Box>
                </Box>
              )}

              {/* View Mode Toggle */}
              <Box
                sx={{
                  mb: 3,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: 'clamp(20px, 2.4vw, 28px)',
                      letterSpacing: '-0.01em',
                      color: '#e8f4fb'
                    }}
                  >
                    {viewMode === 'grouped'
                      ? 'Browse Digital Collectibles'
                      : selectedNftGroup
                        ? `${selectedNftGroup} Listings`
                        : 'Individual Listings'}
                  </Typography>
                  {viewMode === 'grouped' && !groupedSearchTerm && (
                    <Typography variant="body2" color="text.secondary">
                      Click on any Digital Collectibles to view all available listings
                    </Typography>
                  )}
                  {viewMode === 'grouped' && groupedSearchTerm && (
                    <Typography variant="body2" color="text.secondary">
                      {nftGroups.length > 0
                        ? `Found ${nftGroups.length} Digital Collectible${nftGroups.length === 1 ? '' : 's'} matching "${groupedSearchTerm}"`
                        : `No Digital Collectibles found matching "${groupedSearchTerm}"`}
                    </Typography>
                  )}
                  {selectedNftGroup && (
                    <Typography variant="body2" color="text.secondary">
                      Showing all listings for {selectedNftGroup}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  {selectedNftGroup && viewMode === 'individual' && (
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setSelectedNftGroup(null);
                        setViewMode('grouped');
                      }}
                      sx={{
                        borderColor: '#38e8ff',
                        color: '#38e8ff',
                        '&:hover': {
                          borderColor: '#22d3ee',
                          backgroundColor: 'rgba(56, 232, 255, 0.1)'
                        }
                      }}
                    >
                      ← Back to Collections
                    </Button>
                  )}

                  {!selectedNftGroup && (
                    <>
                      {viewMode === 'grouped' && groupedSearchTerm && (
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setGroupedSearchTerm('');
                            fetchGroupedListings('');
                          }}
                          sx={{
                            borderColor: '#38e8ff',
                            color: '#38e8ff',
                            '&:hover': {
                              borderColor: '#22d3ee',
                              backgroundColor: 'rgba(56, 232, 255, 0.1)'
                            }
                          }}
                          startIcon={<CloseIcon />}
                        >
                          Clear Search
                        </Button>
                      )}
                      <Button
                        variant={
                          viewMode === 'grouped' ? 'contained' : 'outlined'
                        }
                        onClick={() => setViewMode('grouped')}
                        sx={{
                          backgroundColor:
                            viewMode === 'grouped' ? '#38e8ff' : 'transparent',
                          borderColor: '#38e8ff',
                          color: viewMode === 'grouped' ? '#04222a' : '#38e8ff',
                          '&:hover': {
                            backgroundColor:
                              viewMode === 'grouped'
                                ? '#22d3ee'
                                : 'rgba(56, 232, 255, 0.1)',
                            borderColor: '#22d3ee'
                          }
                        }}
                      >
                        Grouped View
                      </Button>
                      <Button
                        variant={
                          viewMode === 'individual' ? 'contained' : 'outlined'
                        }
                        onClick={() => setViewMode('individual')}
                        sx={{
                          backgroundColor:
                            viewMode === 'individual'
                              ? '#38e8ff'
                              : 'transparent',
                          borderColor: '#38e8ff',
                          color:
                            viewMode === 'individual' ? '#04222a' : '#38e8ff',
                          '&:hover': {
                            backgroundColor:
                              viewMode === 'individual'
                                ? '#22d3ee'
                                : 'rgba(56, 232, 255, 0.1)',
                            borderColor: '#22d3ee'
                          }
                        }}
                      >
                        Individual View
                      </Button>
                    </>
                  )}
                </Box>
              </Box>

              {/* Show search and filters only for individual view */}
              {viewMode === 'individual' && renderSearchAndFilters()}

              {/* Show filtering status information */}
              {viewMode === 'individual' && hasActiveFilters && (
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    bgcolor: 'rgba(56, 232, 255, 0.1)',
                    borderRadius: 1,
                    border: '1px solid rgba(56, 232, 255, 0.3)'
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: '#38e8ff', fontWeight: 'medium' }}
                  >
                    🔍 Filtering {allListings.length} total active listings
                    {filteredListings.length !== allListings.length && (
                      <span> → Found {filteredListings.length} matches</span>
                    )}
                  </Typography>
                  {filteredListings.length === 0 && (
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', mt: 0.5 }}
                    >
                      No Digital Collectibles match your current filter criteria. Try adjusting
                      or clearing your filters.
                    </Typography>
                  )}
                </Box>
              )}

              <Grid container spacing={3}>
                {(() => {
                  if (viewMode === 'grouped') {
                    // Show grouped NFT cards
                    return nftGroups.map((group) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        key={`group-${group.nft.name}`}
                      >
                        <NftGroupCard
                          group={group}
                          cloudfrontBase={CARD_ART_BASE}
                          formatRarity={formatRarity}
                          onOpenGroup={(nftName) => {
                            setSelectedNftGroup(nftName);
                            setViewMode('individual');
                          }}
                        />
                      </Grid>
                    ));
                  } else {
                    // Show individual listings (existing logic)
                    if (hasActiveFilters) {
                      const itemsPerPage = 12;
                      const startIndex = (currentPage - 1) * itemsPerPage;
                      const endIndex = startIndex + itemsPerPage;
                      const paginatedResults = filteredListings.slice(
                        startIndex,
                        endIndex
                      );

                      return paginatedResults.map((listing) => (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={4}
                          lg={3}
                          key={listing.id}
                        >
                          <NftCard
                            data={listing.nft}
                            isListing
                            listing={listing}
                            cloudfrontBase={CARD_ART_BASE}
                            formatRarity={formatRarity}
                            getRarityColor={getRarityColor}
                            currentUsername={user.username ?? ''}
                            onBuyNow={(l) => {
                              setSelectedListing(l);
                              setIsPurchaseModalOpen(true);
                            }}
                            onListForSale={() => {}}
                          />
                        </Grid>
                      ));
                    } else {
                      return filteredListings.map((listing) => (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={4}
                          lg={3}
                          key={listing.id}
                        >
                          <NftCard
                            data={listing.nft}
                            isListing
                            listing={listing}
                            cloudfrontBase={CARD_ART_BASE}
                            formatRarity={formatRarity}
                            getRarityColor={getRarityColor}
                            currentUsername={user.username ?? ''}
                            onBuyNow={(l) => {
                              setSelectedListing(l);
                              setIsPurchaseModalOpen(true);
                            }}
                            onListForSale={() => {}}
                          />
                        </Grid>
                      ));
                    }
                  }
                })()}
              </Grid>

              {/* No listings message */}
              {viewMode === 'grouped' && nftGroups.length === 0 && (
                <Box
                  sx={{
                    ...panelSx,
                    textAlign: 'center',
                    mt: 3,
                    p: { xs: 5, md: 7 }
                  }}
                >
                  <Typography
                    sx={{ fontWeight: 900, fontSize: 22, color: '#e8f4fb' }}
                  >
                    No Digital Collectibles available
                  </Typography>
                  <Typography
                    sx={{ mt: 1, font: `600 13px ${MONO}`, color: '#8ea3b3' }}
                  >
                    There are currently no Digital Collectibles listed on the
                    marketplace
                  </Typography>
                </Box>
              )}

              {viewMode === 'individual' && filteredListings.length === 0 && (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    {selectedNftGroup
                      ? `No ${selectedNftGroup} listings available`
                      : hasActiveFilters
                        ? 'No Digital Collectibles found matching your criteria'
                        : 'No Digital Collectibles available'}
                  </Typography>
                  {hasActiveFilters && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Try adjusting your filters or clearing them to see all
                      listings
                    </Typography>
                  )}
                  {selectedNftGroup && !hasActiveFilters && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      No {selectedNftGroup} Digital Collectibles are currently listed for sale
                    </Typography>
                  )}
                </Box>
              )}

              {viewMode === 'individual' && totalPages > 1 && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <ArenaPager
                    page={currentPage}
                    totalPages={totalPages}
                    onPrev={() => {
                      const p = Math.max(1, currentPage - 1);
                      (hasActiveFilters
                        ? handleFilteredPageChange
                        : handlePageChange)({} as any, p);
                    }}
                    onNext={() => {
                      const p = Math.min(totalPages, currentPage + 1);
                      (hasActiveFilters
                        ? handleFilteredPageChange
                        : handlePageChange)({} as any, p);
                    }}
                  />
                  {hasActiveFilters && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      Showing {filteredListings.length} filtered results across{' '}
                      {totalPages} pages
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* My Listings Tab */}
          {activeTab === 1 && (
            <Box>
              <Grid container spacing={3}>
                {myListings
                  .filter((listing) => listing.status !== 'fee_pending')
                  .map((listing) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={listing.id}>
                      <MyListingCard
                        listing={listing}
                        cloudfrontBase={CARD_ART_BASE}
                        cancelling={cancellingListings.has(listing.id)}
                        onCancel={handleCancelListing}
                      />
                    </Grid>
                  ))}
              </Grid>

              {myListings.length === 0 && (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    You don't have any active listings
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* List NFT Tab */}
          {activeTab === 2 && (
            <Box>
              {renderSearchAndFilters({ showPriceFilter: false })}

              {userNfts.length > 0 && (
                <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 2 }}>
                  {userNfts.filter((n) => n.owned > 0).length.toLocaleString('en-AU')} different cards,{' '}
                  {userNfts.reduce((total, n) => total + n.owned, 0).toLocaleString('en-AU')} in total. Each card
                  shows how many you hold; a listing carries up to 10 of a card.
                </Typography>
              )}

              <Grid container spacing={3}>
                {filteredUserNfts.map((userNft) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={userNft.nft.id}>
                    <NftCard
                      data={userNft}
                      isListing={false}
                      cloudfrontBase={CARD_ART_BASE}
                      formatRarity={formatRarity}
                      getRarityColor={getRarityColor}
                      currentUsername={user.username ?? ''}
                      onBuyNow={() => {}}
                      onListForSale={(nft) => {
                        setSelectedNft(nft);
                        setListingPrice('');
                        setListingQuantity(1);
                        setQuantityInput('1');
                        setShowPriceCalculator(false);
                        setIsListingModalOpen(true);
                        fetchLastSoldPrice(nft.nft.id);
                      }}
                    />
                  </Grid>
                ))}
              </Grid>

              {filteredUserNfts.length === 0 && userNfts.length > 0 && (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    No Digital Collectibles found matching your criteria.
                  </Typography>
                </Box>
              )}

              {userNfts.length === 0 && (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    You don't own any Digital Collectibles
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Activity Tracking Tab */}
          {activeTab === 3 && (
            <Box>
              <Typography variant="h5" gutterBottom>
                Marketplace Activity & Tracking
              </Typography>

              {renderSearchAndFilters()}

              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: 'rgba(56, 232, 255, 0.1)',
                  borderRadius: 2
                }}
              >
                <Typography variant="h6" color="#38e8ff" gutterBottom>
                  Marketplace Statistics
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2">
                      Total Listings: {marketplaceStats.totalListings}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2">
                      Active Listings: {marketplaceStats.activeListings}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2">
                      Sold Listings: {marketplaceStats.soldListings}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2">
                      Total Volume: {marketplaceStats.totalVolume} INC
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <ActivityGrid
                rows={marketplaceRows}
                cloudfrontBase={CARD_ART_BASE}
                formatRarity={formatRarity}
                getRarityColor={getRarityColor}
              />

              {marketplaceRows.length === 0 && (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    No marketplace activity found
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* List NFT Modal */}
      <ListNftModal
        open={isListingModalOpen}
        onClose={() => {
          setIsListingModalOpen(false);
          setListingPrice('');
          setListingQuantity(1);
          setQuantityInput('1');
          setLastSoldPrice(null);
          setSelectedNft(null);
          setShowPriceCalculator(false);
        }}
        selectedNft={selectedNft}
        listingPrice={listingPrice}
        setListingPrice={setListingPrice}
        listingQuantity={listingQuantity}
        setListingQuantity={setListingQuantity}
        quantityInput={quantityInput}
        setQuantityInput={setQuantityInput}
        isProcessing={isProcessing}
        showPriceCalculator={showPriceCalculator}
        togglePriceCalculator={() => setShowPriceCalculator((v) => !v)}
        loadingLastPrice={loadingLastPrice}
        lastSoldPrice={lastSoldPrice}
        formatRarity={formatRarity}
        // ✅ new actions
        onPayFeeUsingEarnedTokens={handleListUsingEarnedTokens}
        onPayFeeUsingInternalWallet={handleListUsingInternalWallet}
        onPayFeeUsingConnectedWallet={handleListUsingConnectedWallet}
        // ✅ balances
        userIncBalance={user.tokenAmount || '0.00000'}
        userEarnedIncBalance={user.earnedTokens || '0.00000'}
        // ✅ wallet flag for disabling
        hasConnectedWallet={connected && !!publicKey}
        get={get}
      />

      {/* Purchase NFT Modal */}
      <PurchaseNftModal
        open={isPurchaseModalOpen}
        onClose={() => {
          setIsPurchaseModalOpen(false);
          setSelectedListing(null);
        }}
        selectedListing={selectedListing}
        isProcessing={isProcessing}
        formatRarity={formatRarity}
        userIncBalance={user.tokenAmount || '0.00000'}
        userEarnedIncBalance={user.earnedTokens || '0.00000'}
        hasConnectedWallet={connected && !!publicKey}
        onPayUsingEarnedTokens={handlePurchaseUsingEarnedTokens}
        onPayUsingInternalWallet={handlePurchaseUsingInternalWallet}
        onPayUsingConnectedWallet={handlePurchaseUsingConnectedWallet}
      />
    </Box>
    </ThemeProvider>
  );
};

export default Marketplace;
